import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useDialogConfirm } from '@src/components/Common/Dialog';
import CallMediaSingleton from '@src/logic/call/callMedia/CallMediaSingleton';
import generateRoomId from '@src/logic/call/generateRoomId';
import {
  RequestOutgoingCallRequest,
  useRequestOutgoingCallMutation,
} from '@src/reducers/callsAPI';
import { CallOutgoing } from '@src/types/calls';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import useHandleCallMediaError from './useHandleCallMediaError';

const useStartOutgoingCallMedia = () => {
  const { formatMessage } = useIntl();
  const dialogConfirm = useDialogConfirm();
  const handleCallMediaError = useHandleCallMediaError();

  const [
    requestOutgoingCallMutation,
    { error: errorOutgoingCall, isLoading: isLoadingOutgoingCall },
  ] = useRequestOutgoingCallMutation({ fixedCacheKey: 'calls' });

  const startCall = async (
    outgoingCall: Pick<CallOutgoing, 'callee' | 'caller'>
  ): Promise<boolean> => {
    if (isLoadingOutgoingCall) return false;

    const roomId = generateRoomId();

    try {
      const { sessionId, handleId } = await CallMediaSingleton.connect();
      if (!sessionId || !handleId) return false;

      await CallMediaSingleton.joinRoom(roomId);
      const payload: RequestOutgoingCallRequest = {
        callContext: {
          call: {
            caller: outgoingCall.caller,
            callee: outgoingCall.callee,
          },
          handleId,
          sessionId,
          roomId,
        },
      };

      await requestOutgoingCallMutation(payload).unwrap();
    } catch (error) {
      handleCallMediaError(error);
      await CallMediaSingleton.leaveRoom();
      return false;
    }

    return true;
  };

  useEffect(() => {
    const status = (errorOutgoingCall as FetchBaseQueryError)?.status;
    // if (status === 409) setOpenPermissionDialog(true);
    if (status === 500)
      dialogConfirm({
        disableBackdropClose: true,
        title: formatMessage({ id: 'CallPermission.Error.TitleLbl' }),
        description: formatMessage({ id: 'CallPermission.Error.ContentLbl' }),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorOutgoingCall]);

  return startCall;
};

export default useStartOutgoingCallMedia;
